import { formatCurrency, formatNumber } from '@angular/common';
import { Injectable } from '@angular/core';
import { compile } from 'ejs';
import { get, kebabCase, round } from 'lodash';
import { DateTime } from 'luxon';

@Injectable({
    providedIn: 'root',
})
export class IqWorkflowTransformationService {
    splitId(id: string, index: number): string {
        if (!id) return '';

        const parts = id.split('~');
        if (index < 0 || parts.length <= index) return '';

        return id.split('~')[index];
    }

    generateId(idParts: string[]) {
        return idParts.join('~');
    }

    kebabParts(parts: string[]): string[] {
        return parts.map((part: string) => kebabCase(part));
    }

    startCase(str: string) {
        return str[0].toUpperCase() + str.slice(1).toLowerCase();
    }

    upperCase(str: string) {
        return str.toUpperCase();
    }

    lowerCase(str: string) {
        return str.toLowerCase();
    }

    round(value: number, precision: number = 2) {
        return round(value, precision);
    }

    formatNumber(value: number): string {
        return formatNumber(value, 'en-US');
    }

    todaysDateKey() {
        return DateTime.now().toFormat('MM/dd/yy');
    }

    nowISO() {
        return DateTime.now().toISO();
    }

    currency(value: number, withCents: boolean = true): string {
        // return this.getCurrencyLabel(value, withCents);
        return withCents
            ? formatCurrency(value, 'en-US', '$', 'USD')
            : formatCurrency(value, 'en-US', '$', 'USD', '1.0-0');
    }

    toArray(arrayLike: any[]): string {
        if (!Array.isArray(arrayLike)) return '[]';

        return JSON.stringify(arrayLike);
    }

    mdIconLabel(
        icon: string,
        label: string = '',
        filled: boolean = false,
        opts: { classes: string[] } = { classes: [] },
    ): string {
        const classes = opts.classes.join(' ');
        const filledClass = filled ? 'iq-mat-icon-fill' : '';
        return !!label.length
            ? `<span data-uitype='md-icon' class='material-symbols-outlined iq-md-icon-label-icon ${filledClass} ${classes}'>${icon}</span><span class='iq-md-icon-label-text ${classes}'>${label}</span>`
            : `<span data-uitype='md-icon' class='material-symbols-outlined iq-md-icon-label-icon ${filledClass} ${classes}'>${icon}</span>`;
    }
    checkboxLabel(
        label: string,
        icon: string,
        filled: boolean,
        opts: { classes: string[] },
        checked: boolean,
    ): string {
        return checked
            ? checkBoxLabelChecked(label, icon, filled, opts)
            : checkBoxLabelUnchecked(label, icon, filled, opts);
    }

    // Used to search to an arbitrary depth within an object in an EJS expression,
    // returning undefined if any property along the path is undefined.  This is
    // useful where you would normal use optional chaining, which was throwing
    // an error when I attempted it
    maybe(obj: any, path: string) {
        return get(obj, path);
    }

    get(obj: any, path, defaultValue: any = undefined) {
        return get(obj, path, defaultValue);
    }

    /**
     * This is just to help us debug the transformations so we can understand what is going on.
     *
     * @param obj - object to debug This is the object that is being transformed
     * @param title - title of this debug group
     * @param template - the template being used
     * @param data - the data used for debugging
     * @returns the current object
     */
    debug(
        obj: any,
        title: string = '',
        template: string = '',
        data: any = 'No Data',
    ) {
        console.groupCollapsed(!!title?.length ? title : 'DEBUGGING TEMPLATE');
        console.log('OBJECT', obj);
        console.log('DATA', data);
        console.log('TEMPLATE', template);
        console.groupEnd();

        return obj;
    }

    /**
     * This will convert any boolean to a string that can be used to help convert complex if statements to enums
     *
     * @param value - a record of strings to booleans that if the value is true, it will return the key
     */
    boolToString(value: Record<string, boolean>): string {
        return Object.keys(value).find((key) => value[key]) ?? '';
    }

    getTransformations(template: string, data: any = {}) {
        return {
            splitId: (id: string, index: number) => this.splitId(id, index),
            generateId: (idParts: string[]) => this.generateId(idParts),
            startCase: (str: string) => this.startCase(str),
            kebabParts: (parts: string[]) => this.kebabParts(parts),
            upperCase: (str: string) => this.upperCase(str),
            lowerCase: (str: string) => this.lowerCase(str),
            todaysDateKey: () => this.todaysDateKey(),
            toArray: (arrayLike: any[]) => this.toArray(arrayLike),
            nowISO: () => this.nowISO(),
            round: (value: number, precision: number = 2) =>
                this.round(value, precision),
            currency: (value: number, withCents: boolean = true) =>
                this.currency(value, withCents),
            maybe: (obj: any, path: string) => this.maybe(obj, path),
            formatNumber: (value: number) => this.formatNumber(value),
            mdIconLabel: (
                icon: string,
                label: string = '',
                filled: boolean = false,
                opts: { classes: string[] } = { classes: [] },
            ) => this.mdIconLabel(icon, label, filled, opts),
            checkboxLabel: (
                label: string,
                icon: string,
                filled: boolean,
                checked: boolean,
                opts: { classes: string[] } = { classes: [] },
            ) => this.checkboxLabel(label, icon, filled, opts, checked),
            boolToString: (value: Record<string, boolean>) =>
                this.boolToString(value),
            get: (obj: any, path: string, defaultValue = undefined) =>
                this.get(obj, path, defaultValue),
            debug: (obj: any, title: string = '') =>
                this.debug(obj, title, template, data),
        };
    }

    // These anys are okay because this will take in any unknown data and return unknown data
    render(template: string, data: any): any {
        try {
            const render = compile(template)({
                ...data,
                ...this.getTransformations(template, data),
            })
                .replace(/"true"/g, 'true')
                .replace(/"false"/g, 'false')
                .replace(/(\")([0-9]*\.[0-9]*)(\")/gm, '$2')
                .replace(/(\")(\-[0-9]*\.[0-9]*)(\")/gm, '$2')
                .replace(/(\")([0-9]*)(\")/gm, '$2')
                .replace(/("\[")/gm, '["')
                .replace(/("\]")/gm, '"]')
                .replace(/"\[\]"/gm, '[]')
                .replace(/("\[\{)/gm, '[{')
                .replace(/(\}\]")/gm, '}]')
                .replace(/\\"\]/gm, '\\"]"')
                .replace(/(\")(\-[0-9]*)(\")/gm, '$2');
            const currentInfo = JSON.parse(render);

            return currentInfo;
        } catch (e) {
            console.error(e);
            return {};
        }
    }
}

function checkBoxLabelChecked(
    label: string,
    icon: string = 'check_box',
    filled: boolean = true,
    opts: { classes: string[] } = { classes: [] },
): string {
    const classes = opts.classes.join(' ');
    const filledClass = filled ? 'iq-mat-icon-fill' : '';

    return `<span data-uitype='md-icon' class='material-symbols-outlined iq-md-icon-label-icon text-dat-blue-500 ${classes} ${filledClass}'>${icon}</span> <span class='iq-md-icon-label-text'>${label}</span>`;
}

function checkBoxLabelUnchecked(
    label: string,
    icon: string = 'check_box_outline_blank',
    filled: boolean = false,
    opts: { classes: string[] } = { classes: [] },
): string {
    const classes = opts.classes.join(' ');
    const filledClass = filled ? 'iq-mat-icon-fill' : '';

    return `<span data-uitype='md-icon' class='material-symbols-outlined iq-md-icon-label-icon ${classes} ${filledClass}'>${icon}</span> <span class='iq-md-icon-label-text'>${label}</span>`;
}
